import { Button, Text, VSpacer } from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import {
  EnergyProgramEntry,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/Energy/EnergyProgramEntry';
import {
  EnergyProgramModal,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/Energy/EnergyProgramModal';
import {
  EnergyProgramForm,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/Energy/energyValidation';
import {
  EnergyProgramEmptyState, EnergyProgramEmptyStateContext,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/EnergyProgramEmptyState';
import {
  ProgramSectionHeader,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/ProgramSectionHeader';
import {
  compareProgramsByStatusThenEndDate,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/RewardsPrograms/helpers';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { EnergyApi } from '@/utilities/api/EnergyApi';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { ApiEnergyProgramDetail } from '@api/interfaces/ApiEnergyProgramDetail';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import Add from '@mui/icons-material/Add';
import { Divider, Stack } from '@mui/material';
import { ProgramStatus } from '@shared/enums/ProgramStatus';
import { RewardsProgramType } from '@shared/enums/RewardsProgramType';
import { getProgramStatus } from '@shared/utilities';
import { DateTime } from 'luxon';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

interface EnergyProgramCardProps {
  categoryMappingCard?: ReactNode,
  context?: EnergyProgramEmptyStateContext,
  programs?: ApiRewardsProgram[],
  retailerId: string,
}

export const EnergyProgramCard = ({
  categoryMappingCard,
  context = 'program',
  programs,
  retailerId,
}: EnergyProgramCardProps) => {
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [createdProgramId, setCreatedProgramId] = useState<string | null>(null);
  const energyPrograms = (programs ?? [])
    .filter(({ type }) => type === RewardsProgramType.Energy)
    .sort(compareProgramsByStatusThenEndDate);
  const [mostRelevantProgram] = energyPrograms;
  // Like agronomy, one upcoming program year waits at a time.
  const canAddProgram = !energyPrograms.some((program) => (
    getProgramStatus(program) === ProgramStatus.Future
  ));
  const latestEndDate = energyPrograms.map(({ endDate }) => endDate).sort().reverse()[0];
  const nextStartDate = latestEndDate
    ? DateTime.fromISO(latestEndDate).plus({ days: 1 }).toISODate() ?? undefined
    : undefined;

  const { data: categoryMappings } = useQuery(
    [QueryKeys.GET_RETAILER_CATEGORY_MAPPINGS, retailerId],
    () => RetailerApi.getCategoryMappings(retailerId),
  );

  const refresh = async (detail?: ApiEnergyProgramDetail) => {
    if (detail) {
      queryClient.setQueryData([QueryKeys.GET_ENERGY_PROGRAM, detail.id], detail);
    }
    await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_BY_ID);
    await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_CATEGORY_MAPPINGS);
  };

  const closeAddModal = () => {
    setApiError(null);
    setIsAdding(false);
  };

  const { isLoading: isCreating, mutate: createProgram } = useMutation(
    (form: Required<EnergyProgramForm>) => EnergyApi.createProgram({
      ...form,
      endDate: form.endDate!,
      name: form.name.trim(),
      pointsExpirationDate: form.pointsExpirationDate!,
      retailerId,
      startDate: form.startDate!,
    }),
    {
      onError: (error: { message?: string }) => {
        setApiError(error.message || t(($) => $.energy.programModal.errors.generic));
      },
      onSuccess: async (detail) => {
        openSnackbar(t(($) => $.energy.programModal.created));
        setCreatedProgramId(detail.id);
        await refresh(detail);
        closeAddModal();
      },
    },
  );

  const addModal = isAdding && (
    <EnergyProgramModal
      apiError={apiError}
      categoryMappings={categoryMappings ?? []}
      defaultStartDate={nextStartDate}
      isSaving={isCreating}
      onClose={closeAddModal}
      onSave={(form) => createProgram(form)}
      otherEnergyPrograms={energyPrograms}
    />
  );

  if (!mostRelevantProgram) {
    return (
      <>
        <Divider />
        <EnergyProgramEmptyState
          context={context}
          onAdd={() => setIsAdding(true)}
          testID="energy-program-empty-state"
        />
        {!!categoryMappingCard && (
          <Stack sx={{ p: '20px' }}>{categoryMappingCard}</Stack>
        )}
        {addModal}
      </>
    );
  }

  return (
    <>
      <Divider />
      <ProgramSectionHeader name={mostRelevantProgram.name} type={RewardsProgramType.Energy} />
      <Stack sx={{ p: '20px' }}>
        {!!categoryMappingCard && (
          <>
            {categoryMappingCard}
            <VSpacer size="6" />
          </>
        )}
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Text category="title-small">
            REWARDS PROGRAMS
          </Text>
          {canAddProgram && (
            <Button
              onClick={() => setIsAdding(true)}
              startIcon={<Add />}
              testID="energy-program-add-button"
              variant="outlined"
            >
              {t(($) => $.common.actions.add)}
            </Button>
          )}
        </Stack>
        <VSpacer size="4" />
        <Stack gap="16px">
          {energyPrograms.map((program) => (
            <Stack key={program.id}>
              <EnergyProgramEntry
                categoryMappings={categoryMappings ?? []}
                energyPrograms={energyPrograms}
                isNewProgram={program.id === createdProgramId}
                onRefresh={refresh}
                program={program}
              />
            </Stack>
          ))}
        </Stack>
      </Stack>
      {addModal}
    </>
  );
};
